/**
 * Walking the camera with the arrow keys, or WASD.
 *
 * Up walks forward, Down back, Left and Right strafe — all of it relative to
 * where the camera is FACING, so at street level with the city leaning over
 * you the keys do what your body would expect rather than what the screen
 * axes would.
 *
 * ## WASD is the same four keys, not a second set
 *
 * Both sets resolve to one canonical token before anything else sees them, so
 * W and Up are literally the same held key. Holding both is holding one, which
 * is the only sane answer — summing them would walk at double speed for no
 * reason a player could name.
 *
 * ## Superseding MapLibre rather than switching it off
 *
 * MapLibre's own keyboard handler is on, and it already claims the arrows: an
 * unmodified arrow becomes a 100px `easeTo` over 300ms. Two things make that
 * the wrong thing to leave running. It re-eases from the current centre on
 * every key repeat, which reads as a stutter rather than a glide, and its step
 * is screen-space, so under pitch the same keypress covers wildly different
 * ground depending on how far up the screen the horizon is.
 *
 * It cannot be turned off by halves — `map.keyboard.disable()` would take the
 * +/- zoom and the Shift-rotate with it. And `preventDefault()` alone does
 * nothing, because its handler never looks at `defaultPrevented`.
 *
 * What does work is where it listens: on the map's canvas container, in the
 * bubble phase. A capture-phase listener on `window` runs first, so calling
 * `stopPropagation()` there means the event never reaches it. Only the four
 * unmodified arrows are taken. Shift-arrow still rotates and pitches, +/-
 * still zooms, and neither needed replacing.
 */

import { isShortcut } from "./keys.js";

/**
 * On-screen travel per second, in pixels — not metres.
 *
 * Deliberately a screen speed. Scaled by metres-per-pixel at the current zoom
 * it means one held key covers the same visible distance whether you are down
 * among the doorways or looking at the whole city, which is what makes the
 * same two keys usable as a street-level walk and as a fine nudge at altitude.
 * A metre-based speed would be imperceptible at z12 and violent at z19.
 *
 * This was 110 — a walking pace, chosen so a held key covered about a city
 * block. It is a sprint now because that is what was asked for, and measured
 * rather than guessed: at 260 px/s a one-and-a-half-second hold covers 271
 * metres at z17.4, which is the visible scene at street level and then some.
 *
 * The fine nudge survives the speed, which is the part worth checking. Because
 * `EASE_MS` ramps the velocity rather than applying it whole, a tap still only
 * travels ~24 pixels — the extra speed lands on held keys, where it was wanted,
 * and not on single presses, where it would have made the map twitchy.
 */
export const SPEED_PX_PER_S = 260;

/**
 * Time constant for reaching, and shedding, full speed.
 *
 * The glide is the whole point. Key-repeat arrives in bursts with a long gap
 * before the first repeat, so moving on the keydown events themselves lurches;
 * this drives a velocity from a requestAnimationFrame loop instead, and the
 * keys only set the target. Short enough that a tap still reads as a nudge.
 */
const EASE_MS = 110;

/** Below this the coast has stopped and the loop can stand down. */
const STOP = 0.002;

/** A frame after a stall is not a licence to teleport across the city. */
const MAX_FRAME_MS = 64;

/** Metres per degree of latitude. Close enough anywhere Denver is. */
const M_PER_DEG = 111320;

/** Web Mercator ground resolution at zoom 0, in metres per pixel. */
const EQUATOR_M_PER_PX = 156543.03392;

const FORWARD_KEYS = { ArrowUp: 1, ArrowDown: -1 };
const STRAFE_KEYS = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * WASD, by physical position rather than by letter.
 *
 * `event.code` is the key's place on the board; `event.key` is the character it
 * produced. Using position is what every game does, and here it also removes a
 * bug rather than merely matching convention: `event.key` for the W key is "w"
 * normally and "W" under Shift or Caps Lock. Since a key is held in a Set from
 * keydown until keyup, a player who pressed W and then happened to touch Shift
 * would release "W" against a Set holding "w" — the delete would miss and the
 * camera would walk away on its own, with no key held and no way to stop it.
 * `event.code` is "KeyW" through all of that.
 *
 * The cost is Dvorak and AZERTY, where these are not the letters W/A/S/D. That
 * is the right trade: the diamond under the left hand is the thing being bound,
 * and it is in the same place on every board.
 */
const WASD = {
  KeyW: "ArrowUp",
  KeyS: "ArrowDown",
  KeyA: "ArrowLeft",
  KeyD: "ArrowRight",
};

/**
 * The canonical arrow token this event drives, or null if it drives none.
 *
 * Everything downstream — the held Set, the axis sums — speaks only in arrow
 * names, so WASD is translated once, here, and never again.
 */
export function movementKey(event) {
  if (!event) return null;
  if (event.key in FORWARD_KEYS || event.key in STRAFE_KEYS) return event.key;
  return WASD[event.code] ?? null;
}

/** Ground metres covered by one screen pixel, here and at this zoom. */
export function metresPerPixel(zoom, lat) {
  return (EQUATOR_M_PER_PX * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
}

/**
 * How far to move the centre, in degrees, for a step along the bearing.
 *
 * Bearing is the compass direction that is "up" on screen, so forward is that
 * direction on the ground and strafing right is 90 degrees clockwise of it.
 * Working on the ground plane rather than in screen space is what keeps a step
 * the same size whether the camera is flat or leaning at 70 degrees — a
 * screen-space pan under pitch moves you much further at the top of the frame
 * than the bottom, for the same pixels.
 */
export function stepOffset({ lat, bearing, zoom, forward = 0, strafe = 0, pixels }) {
  const metres = pixels * metresPerPixel(zoom, lat);
  const radians = (bearing * Math.PI) / 180;
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);

  const east = forward * sin + strafe * cos;
  const north = forward * cos - strafe * sin;

  const cosLat = Math.cos((lat * Math.PI) / 180);
  return {
    // At the poles a degree of longitude is worth nothing and the division
    // runs away. Nowhere near Denver, but a camera that can be flown is a
    // camera that can be flown somewhere silly.
    dLng: Math.abs(cosLat) < 1e-6 ? 0 : (east * metres) / (M_PER_DEG * cosLat),
    dLat: (north * metres) / M_PER_DEG,
  };
}

export function mountWalk({ map } = {}) {
  if (!map) return { destroy() {} };

  const held = new Set();
  let forwardVelocity = 0;
  let strafeVelocity = 0;
  let frame = 0;
  let last = 0;

  const axis = (keys) => Object.entries(keys)
    .reduce((total, [key, sign]) => total + (held.has(key) ? sign : 0), 0);

  function step(timestamp) {
    frame = 0;
    const elapsed = Math.min(timestamp - last, MAX_FRAME_MS);
    last = timestamp;

    const targetForward = axis(FORWARD_KEYS);
    const targetStrafe = axis(STRAFE_KEYS);
    // Exponential approach: framerate-independent, and the same curve slows
    // the camera on release as sped it up on press.
    const rate = 1 - Math.exp(-elapsed / EASE_MS);
    forwardVelocity += (targetForward - forwardVelocity) * rate;
    strafeVelocity += (targetStrafe - strafeVelocity) * rate;

    const moving = Math.abs(forwardVelocity) > STOP
      || Math.abs(strafeVelocity) > STOP;
    if (!moving && held.size === 0) {
      forwardVelocity = 0;
      strafeVelocity = 0;
      return;
    }

    const centre = map.getCenter();
    const { dLng, dLat } = stepOffset({
      lat: centre.lat,
      bearing: map.getBearing(),
      zoom: map.getZoom(),
      forward: forwardVelocity,
      strafe: strafeVelocity,
      pixels: SPEED_PX_PER_S * (elapsed / 1000),
    });
    // setCenter, not easeTo: this loop IS the animation, and handing each
    // frame to an easing of its own would have 60 of them fighting a second.
    map.setCenter([centre.lng + dLng, centre.lat + dLat]);

    frame = requestAnimationFrame(step);
  }

  function start() {
    if (frame) return;
    last = performance.now();
    frame = requestAnimationFrame(step);
  }

  function onKeyDown(event) {
    // Same guard as every other global shortcut: typing "down" into the mark
    // editor must stay typing. It also clears Ctrl and Meta, which is what
    // keeps Ctrl-W closing the tab rather than walking north.
    if (!isShortcut(event)) return;

    const key = movementKey(event);
    if (!key) return;

    // Shift-arrow is MapLibre's rotate and pitch, which nothing here replaces.
    //
    // WASD is deliberately exempt. Shift means nothing to MapLibre on a letter
    // key, so there is nothing to protect — and a player reaching for Shift to
    // sprint would otherwise find the keys go dead in their hand, which is the
    // opposite of what that reflex expects. Shift-W walks. There is no faster
    // gear to shift into because the ordinary speed is already the sprint.
    //
    // An arrow is the one case where the event's own key is already canonical.
    const pressedAnArrow = event.key === key;
    if (event.shiftKey && pressedAnArrow) return;

    event.preventDefault();
    // The one that actually matters: MapLibre listens on the canvas container
    // in the bubble phase, and this runs at window in the capture phase.
    event.stopPropagation();

    held.add(key);
    start();
  }

  function onKeyUp(event) {
    // Released keys only clear the target. The velocity still has to decay,
    // which is what turns a let-go into a coast instead of a full stop.
    //
    // Never guarded on modifiers, unlike keydown: a release has to be honoured
    // whatever is being held alongside it, or the key stays down forever.
    const key = movementKey(event);
    if (key) held.delete(key);
  }

  // A key held while the window loses focus never delivers its keyup, and the
  // camera would drift away under a tab nobody is looking at.
  function onBlur() {
    held.clear();
  }

  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("keyup", onKeyUp, true);
  window.addEventListener("blur", onBlur);

  return {
    destroy() {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", onBlur);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      held.clear();
    },
  };
}
